'use client'

import { usePathname, useSearchParams } from 'next/navigation'
import {
  createContext,
  type FC,
  type ReactNode,
  useMemo,
  useState,
} from 'react'

export enum LOGIN_OTP_STATE {
  LOGIN_REGISTER = 'login',
  OTP_INPUT = 'otp_input',
  OTP_REQUEST = 'otp_request',
  OTP_REQUEST_NEW_CODE = 'otp_request_new_code',
}

interface LoginRegistrationUserLoginState {
  isLoginPage: boolean
  loginOtpState: LOGIN_OTP_STATE
  otpQuery: string
  setLoginOtpState: (loginOtpState: LOGIN_OTP_STATE) => void
  setUsername: (username: string) => void
  username: string
}

// eslint-disable-next-line @typescript-eslint/no-empty-function -- the rule was disabled before a comment became required
const noop = (): void => {}

export const LoginRegistrationUserLoginStateContext =
  createContext<LoginRegistrationUserLoginState>({
    isLoginPage: false,
    loginOtpState: LOGIN_OTP_STATE.LOGIN_REGISTER,
    otpQuery: '',
    setLoginOtpState: noop,
    setUsername: noop,
    username: '',
  })

interface LoginRegistrationUserLoginStateProviderProps {
  children: ReactNode
  guestEmail?: string
}

export const LoginRegistrationUserLoginStateProvider: FC<
  LoginRegistrationUserLoginStateProviderProps
> = ({ children, guestEmail }) => {
  const pathname = usePathname()
  const isLoginPage = pathname.includes('/login')

  // if on login page use the query params to set the initial state
  const searchParams = useSearchParams()
  const emailQuery = isLoginPage
    ? (searchParams.get('email') ?? guestEmail ?? '')
    : ''
  const otpQuery = isLoginPage ? (searchParams.get('otp') ?? '') : ''
  const otpState = isLoginPage
    ? ((searchParams.get('otpState') as LOGIN_OTP_STATE | null) ??
      LOGIN_OTP_STATE.LOGIN_REGISTER)
    : LOGIN_OTP_STATE.LOGIN_REGISTER

  const [loginOtpState, setLoginOtpState] = useState<LOGIN_OTP_STATE>(otpState)
  const [username, setUsername] = useState(emailQuery)

  const value = useMemo(
    () => ({
      isLoginPage,
      loginOtpState,
      otpQuery,
      setLoginOtpState,
      setUsername,
      username,
    }),
    [
      loginOtpState,
      setLoginOtpState,
      username,
      otpQuery,
      setUsername,
      isLoginPage,
    ],
  )

  return (
    <LoginRegistrationUserLoginStateContext.Provider value={value}>
      {children}
    </LoginRegistrationUserLoginStateContext.Provider>
  )
}
